import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, first } from "rxjs/operators";
import { VideoRecordingSetupMessage } from "../messages/xsight";
import { DeviceScale, IMonitorInfo } from "app/modules/remote-control-ng/definitions";
import { MathHelpers } from "../utils/math";

import { RcContext } from "./rcContext";
import { ElementRect, ISkinInformation } from "../mcrc-api";
import { DeviceContext } from "./deviceContext";

export class MultiScreenContext {
    //Here we capture the monitor infos
    private _monitorList: IMonitorInfo[] = [];
    // only used for request a new monitor in sending restart message
    private _requestMonitorId = 0;
    private _requestMonitorId$ = new BehaviorSubject(0);
    private _remoteControlDisplayChanged = new Subject<number>();
    private _savedMonitor = 0;
    // used for compatibility between old windows agent and latest windows agent
    private _supportWinMonitorList = false;

    // ---------------------------------
    // here we handle monitors related (current only android)
    public get monitorList(): IMonitorInfo[] { return this._monitorList; }
    public set monitorList(monitorList: IMonitorInfo[]) { this._monitorList = monitorList; }

    public get requestMonitorId(): number { return this._requestMonitorId; }

    public set requestMonitorId(id: number) {
        this._requestMonitorId = id;
        this._requestMonitorId$.next(this._requestMonitorId);
    }

    public get supportWinMonitorList(): boolean { return this._supportWinMonitorList; }
    public set supportWinMonitorList(value: boolean) { this._supportWinMonitorList = value; }

    public inVisualSelectionMode: boolean = false;

    public get savedMonitor(): number { return this._savedMonitor; }
    public set savedMonitor(id: number) { this._savedMonitor = id; }

    /** Transfer monitor info (e.g. current monitor id, list of monitors)  when transferring the RC session */
    public copy = (oldMultiView: MultiScreenContext): void => {
        this._monitorList = oldMultiView.monitorList;
        this.supportWinMonitorList = oldMultiView.supportWinMonitorList;
        const monitorId = this.inVisualSelectionMode ? this.savedMonitor : oldMultiView.requestMonitorId;
        const monitor = this._monitorList.find((m) => m.id === monitorId);
        if (monitor) {
            this.requestMonitorId = monitorId;
        }
    }

    public get requestMonitorId$(): BehaviorSubject<number> { return this._requestMonitorId$; }

    public remoteControlDisplayChanged$: Observable<number> = this._remoteControlDisplayChanged.asObservable();

    public updateMonitorInfo = (monitor: IMonitorInfo, resetZoom = false) => {
        // console.log("------- updateMonitorInfo: " + monitor.id + "-------");
        const ctx = RcContext.instance.deviceContext as DeviceContext
        const deviceContextHandler = RcContext.instance.deviceContextHandler;
        const onSkinLoaded = () => {
            if (monitor.id > 0) {
                const skin = deviceContextHandler.currentContext().skin;
                const message = new VideoRecordingSetupMessage({
                    id: 'Monitor-' + monitor.id,
                    deviceWidth: ctx.skinScreenWidth,
                    deviceHeight: ctx.skinScreenHeight,
                    rotation: monitor.rotation,
                    hasNotchImage: skin.isIrregular,
                    screen: skin.screen as ElementRect
                } as ISkinInformation);
                RcContext.instance.session.sendMessage(message);
            }
        };

        // clear previous display infos
        deviceContextHandler.updateWithSessionEnd();

        // update deviceContext with new width and height

        // Make sure to update when there is a valid rotation
        monitor.rotation$
            .pipe(filter(rotation => rotation !== undefined && rotation !== null), first())
            .subscribe(rotation => {
                let width = monitor.width;
                let height = monitor.height;
                if (MathHelpers.isOdd(rotation)) {
                    var tmpWidth = width;
                    width = height;
                    height = tmpWidth;
                }
                ctx.deviceHeight = height;
                ctx.deviceWidth = width;
                ctx.screenHeight = height;
                ctx.screenWidth = width;
                ctx.rotation = rotation;
                ctx.targetRotation = rotation;
                ctx.screenOffsetX = 0;
                ctx.screenOffsetY = 0;
                ctx.saveScale = 1;

                // TODO 2025.1: should we check if meaningless updates are done dirtying transitions and avoid updates if hasItChanged
                // set to auto zoom
                if (resetZoom) {
                    RcContext.instance.deviceScreenTransformService.setTargetScale(DeviceScale.ScaleAuto);
                }

                // update deviceContextHandler with new width and height
                deviceContextHandler.updateWithNewMonitor(monitor);

                // update skin related (For right now, it only supports no skin)
                RcContext.instance.session.updateDeviceSkinController(monitor.id, onSkinLoaded);
            });

        deviceContextHandler.updateWithNewMonitor(monitor);
        RcContext.instance.session.updateDeviceSkinController(monitor.id, undefined);
    }

    public selectDeviceDisplayById(monitorId: number, force = false): void {
        const previousMonitorId = this.requestMonitorId;
        this.requestMonitorId = monitorId;
        if ((previousMonitorId != monitorId) || force) {
            const monitor = this.monitorList.find(m => m.id === monitorId);
            if (monitor) {
                // check whether it is new RC or old RC
                // if (videoStreaming) {
                // TODO - new RC
                // check if is recording, cannot switch
                // if (this.isRecording) {
                //   return;
                // }
                // pause the rc Session
                // remote-control.connection.ts onStatusUpdate() update the device info
                // need to update the case for the nxt-remote-control-screen.service.ts onConnectionStatusUpdate()
                //    need to update the deviceInfo.skinName to empty string for secondary monitor
                //    or just check if when switch to the secondary, set the skinName to the empty string, no need to memo the original skinName
                // enable the autoZoomed,
                // How to send message to agent
                //    1. send full screen nxt-remote-control-screen.service.ts scale()
                //    2. send restart by using applyChange(), but need to compute the new scale, logic is in the updateRcScreenConfiguration()
                // throw new Error("new RC enabled, Not Implemented");
                // return;
                // }
                this.updateMonitorInfo(monitor, true);
                this._remoteControlDisplayChanged.next(monitorId);
            }
        }
    }
}
